import type { CartInfo } from "@api/getCartInfo";
import type { ShipmentRate, ShippingAddressType } from "@constants/formDefaults";
import { gql } from "graphql-request";
import purchaseGraphqlClient from "./purchaseGraphqlClient";

export const AUTH_UPDATE_PURCHASE = gql`
  mutation AUTH_UPDATE_PURCHASE($uuid: String!, $params: AuthUpdatePurchaseInput!) {
    auth_update_purchase(uuid: $uuid, params: $params) {
      uuid
      stripe_invoice
      stripe_invoice_id
      cart_info {
        currency
        subtotal
        total_amount
        applied_discounts {
          discount_amount
          description
          coupon_id
        }
        tax
        items {
          product_title
          product_description
          sku
          frequency
          quantity
          price_amount
          unit_amount
          type
        }
        bundles {
          product_title
          product_description
          sku
          frequency
          quantity
          price_amount
          unit_amount
          type
        }
      }
    }
  }
`;

export interface AuthUpdatePurchaseInput {
  email?: string;
  firstName?: string;
  lastName?: string;
  organizationName?: string;
  items?: Array<{ sku: string; quantity: number }>;
  billing_address?: {
    postal_code?: string;
  };
  shipping_address?: ShippingAddressType;
  shipment_rate?: ShipmentRate;
  professional_credentials?: string;
  coupon_id?: string;
  promotion_code?: string;
  BIData?: {
    source: string;
  };
}

export interface AuthUpdatePurchaseResponse {
  uuid: string;
  stripe_invoice?: {
    tax?: number;
    automatic_tax?: {
      enabled: boolean;
      status: string;
    };
    [key: string]: unknown;
  };
  stripe_invoice_id?: string;
  cartInfo?: CartInfo;
  cart_info?: CartInfo;
  totalTax?: number;
  updateError?: string;
}

interface AuthUpdatePurchaseGQLResponse {
  auth_update_purchase: AuthUpdatePurchaseResponse;
}

export async function authUpdatePurchase(
  token: string | null,
  uuid: string,
  params: AuthUpdatePurchaseInput,
): Promise<AuthUpdatePurchaseResponse> {
  if (!token) {
    throw new Error("Authentication token is required for authenticated purchase update");
  }

  const data = await purchaseGraphqlClient.request<AuthUpdatePurchaseGQLResponse>(
    AUTH_UPDATE_PURCHASE,
    { uuid, params },
    token,
  );

  const update = data.auth_update_purchase;

  // Process tax calculation similar to regular updatePurchase
  const rawStripeTax = update?.stripe_invoice?.tax; // Stripe returns amounts in cents
  const taxEnabled = update?.stripe_invoice?.automatic_tax?.enabled === true;
  const taxComplete = update?.stripe_invoice?.automatic_tax?.status === "complete";

  const updateError =
    (taxEnabled &&
      !taxComplete &&
      `Unable to calculate tax. Please verify your zip code and try again.`) ||
    undefined;

  // Ensure cart_info.tax is only set when Stripe automatic tax is enabled and complete
  const computedTax =
    taxEnabled && taxComplete && typeof rawStripeTax === "number" ? rawStripeTax / 100 : undefined;
  const transformedCartInfo = update?.cart_info
    ? {
        ...update.cart_info,
        tax: computedTax,
        // Preserve server-provided items and bundles for display
        items: update.cart_info.items ?? [],
        bundles: update.cart_info.bundles ?? [],
      }
    : update?.cart_info;

  return {
    ...update,
    cart_info: transformedCartInfo,
    totalTax: computedTax,
    updateError: updateError || undefined,
  };
}
